import "../styles/default.scss";

import classNames from "classnames";
import React, { useRef, useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { compose } from "redux";

import withTranslations from "../../../../../../hocs/withTranslations";
import LandingTypography from "../../../components/LandingTypography";
import config from "../../../../../../config";
import ChevronUp from "../../../components/LandingTypography/assets/chevron-up.svg";
import FormatCurrency from "../../Homepage/components/FormatCurrency";
import PaymentMethod from "./PaymentMethod";
import withUserData from "../../../../../../hocs/withUserData";
import useOnPaymentButtonClick, {
  getPaymentMethodPricing,
  useIsProceedToPayment
} from "../utils/useOnPaymentButtonClick";
import PaymentBarTooltip from "./PaymentBarTooltip/PaymentBarTooltip";
import isDowngrading from "../utils/isDowngrading";
import convertSecondsToDaysToDays from "../utils/convertSecondsToDays";
import { createPortal } from "react-dom";
import PaymentModal from "./PaymentModal";
import InvoiceFrom from "./InvoiceForms/components/InvoiceForm";
import Checkbox from "../../../../../../lib/Checkbox/Checkbox";
import withInvoices from "../../../../../../hocs/withInvoices";
import PaymentButton from "./PaymentButton";
import { resolveInvoiceFormDataForPayment } from "../../../../../../lib/payment/resolveInvoiceFormDataForPayment";

const PaymentBarCompacted = ({
  selectedPlan,
  currentCurrencyOfferByPeriod,
  availablePaymentMethods,
  locale,
  translate,
  history,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  finalizePayment,
  paymentFormLoading,
  paymentDetailsLoading,
  paymentDetails,
  userData,
  fetchSavedInvoiceData,
  invoicesLoading,
  savedInvoicesEnabled
}) => {
  const invoiceFromRef = useRef(null);
  const hasSupportedPaymentMethod = availablePaymentMethods.length > 0;
  const isFree = currentCurrencyOfferByPeriod.offer_amount === 0;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const invoicesEnabled = config.invoicesEnabled;
  const { onPaymentButtonClick } = useOnPaymentButtonClick({
    finalizePayment,
    selectedPaymentMethod,
    currentCurrencyOfferByPeriod,
    locale,
    userData,
    history,
    selectedPlan
  });
  const paymentMethodPricing = getPaymentMethodPricing({
    selectedPaymentMethod,
    currentCurrencyOfferByPeriod
  });

  const isProceedToPayment = useIsProceedToPayment();

  useEffect(() => {
    if (invoicesEnabled) {
      fetchSavedInvoiceData();
    }
  }, []);

  useEffect(() => {
    if (!invoicesEnabled) {
      return;
    }

    setShowInvoiceForm(savedInvoicesEnabled);
  }, [savedInvoicesEnabled, invoicesEnabled]);

  if (
    isProceedToPayment ||
    paymentDetailsLoading ||
    (invoicesEnabled && invoicesLoading)
  ) {
    return null;
  }

  const isSelectedPackageSubscription =
    selectedPaymentMethod?.requiresSubscriptionId === true;
  const hasSubtractedAmount =
    Number(paymentMethodPricing?.subtracted_amount) > 0;
  const hasOrderMonthlyAmount =
    Number(paymentMethodPricing?.order_monthly_amount) > 0;
  const hasSubscription = paymentDetails?.has_subscription;

  const selectedPlanName = (
    <div className={"flex gap-2 align-center mt-4"}>
      <span className={"text-xs text-black opacity-65"}>
        {translate("plans_payment-bar_current-plan")}
      </span>
      <strong className={"text-base text-black font-bold"}>
        {selectedPlan.name}
      </strong>
    </div>
  );

  const detailPrice = (
    <div className={"flex align-center"}>
      <span className={"text-base text-black opacity-70 font-bold"}>
        <span>
          <FormatCurrency
            locale={locale}
            value={paymentMethodPricing?.offer_amount ?? 0}
          />
        </span>
        <span className={"px-1"}>-</span>
        <span>
          <FormatCurrency
            locale={locale}
            value={paymentMethodPricing?.subtracted_amount ?? 0}
          />
        </span>
        <span className={"text-xs.1 font-medium px-1"}>
          {translate("plans_payment-bar_includes-discounts")}
        </span>
        <PaymentBarTooltip
          prorationAmount={paymentMethodPricing?.proration_amount}
          affiliationDiscountAmount={
            paymentMethodPricing?.affiliation_discount_amount
          }
          discountAmount={paymentMethodPricing?.discount_amount}
          subtractedAmount={paymentMethodPricing?.subtracted_amount}
        />
      </span>
      <strong className={"text-base text-black font-bold mr-3"}>
        {hasSubtractedAmount ? <span className={"px-1.5"}>=</span> : null}
        <FormatCurrency
          locale={locale}
          value={paymentMethodPricing?.order_amount ?? 0}
        />
      </strong>
    </div>
  );

  const description =
    currentCurrencyOfferByPeriod && !isFree ? (
      userData ? (
        <span
          className={"block text-xs text-black opacity-70"}
          dangerouslySetInnerHTML={{
            __html: isSelectedPackageSubscription
              ? isDowngrading({
                  userCurrentPlan: userData?.current_plan,
                  selectedPlan
                })
                ? translate(
                    `plans_payment-bar_downgrading_current-plan_subscription %{offerAmount} %{orderAmount} %{days} %{newPlanName} %{userCurrentPlanName} %{userCurrentDays}`,
                    {
                      offerAmount: FormatCurrency({
                        locale: locale,
                        value: paymentMethodPricing?.offer_amount ?? 0
                      }),
                      orderAmount: FormatCurrency({
                        locale: locale,
                        value: paymentMethodPricing?.order_amount ?? 0
                      }),
                      days: currentCurrencyOfferByPeriod.days,
                      newPlanName: selectedPlan?.name,
                      userCurrentPlanName: userData?.current_plan?.name,
                      userCurrentDays: convertSecondsToDaysToDays(
                        paymentDetails?.end_date_in_seconds
                      )
                    }
                  )
                : translate(
                    `plans_payment-bar_current-plan_subscription %{orderAmount} %{offerAmount} %{days}`,
                    {
                      orderAmount: FormatCurrency({
                        locale: locale,
                        value: paymentMethodPricing?.order_amount ?? 0
                      }),
                      offerAmount: FormatCurrency({
                        locale: locale,
                        value: paymentMethodPricing?.offer_amount ?? 0
                      }),
                      days: currentCurrencyOfferByPeriod.days
                    }
                  )
              : translate(
                  `plans_payment-bar_current-plan_non_subscription %{orderAmount} %{offerAmount} %{days}`,
                  {
                    orderAmount: FormatCurrency({
                      locale: locale,
                      value: paymentMethodPricing?.order_amount ?? 0
                    }),
                    offerAmount: FormatCurrency({
                      locale: locale,
                      value: paymentMethodPricing?.offer_amount ?? 0
                    }),
                    days: currentCurrencyOfferByPeriod.days
                  }
                )
          }}
        ></span>
      ) : null
    ) : (
      <span
        className={"text-xs text-black opacity-70 text-left"}
        dangerouslySetInnerHTML={{
          __html: isDowngrading({
            userCurrentPlan: userData?.current_plan,
            selectedPlan
          })
            ? hasSubscription
              ? translate(
                  `plans_payment-bar_subscription_downgrade-to-free %{userCurrentPlanName} %{newPlanName} %{days}`,
                  {
                    userCurrentPlanName: userData?.current_plan?.name,
                    newPlanName: selectedPlan?.name,
                    days: convertSecondsToDaysToDays(
                      paymentDetails?.end_date_in_seconds
                    )
                  }
                )
              : translate(
                  "plans_payment-bar_no-subscription_downgrade-to-free %{userCurrentPlanName} %{newPlanName} %{days}",
                  {
                    userCurrentPlanName: userData?.current_plan?.name,
                    newPlanName: selectedPlan?.name,
                    days: convertSecondsToDaysToDays(
                      paymentDetails?.end_date_in_seconds
                    )
                  }
                )
            : translate("plans_payment-bar_free")
        }}
      />
    );

  return (
    <>
      {isCollapsed ? null : (
        <div
          className={"payment-bar-backdrop"}
          onClick={() => {
            setIsCollapsed(true);
          }}
        />
      )}
      <LandingTypography className={"payment-bar landing-typography_dark"}>
        <form>
          <div className={"py-3 px-3.6 bg-white"}>
            <div className={"flex gap-2"}>
              <div className={"flex gap-1.5 align-center flex-wrap"}>
                <span className={"text-xs text-black opacity-65"}>
                  {translate("plans_payment-bar_price")}
                </span>
                <strong className={"text-base text-black font-bold"}>
                  <FormatCurrency
                    locale={locale}
                    value={paymentMethodPricing?.order_amount ?? 0}
                  />
                </strong>
                {hasOrderMonthlyAmount ? (
                  <span className={"text-sm text-black opacity-75"}>
                    <span className={"font-semibold"}>
                      (
                      <span className={"font-bold"}>
                        <FormatCurrency
                          locale={locale}
                          value={paymentMethodPricing.order_monthly_amount}
                        />
                      </span>
                    </span>
                    <span>{translate("plans_payment-bar_30-days")}</span>
                    <span className={"text-semibold"}>) </span>
                  </span>
                ) : null}
              </div>
              <button
                type={"button"}
                className={
                  "ml-auto bg-none b-none text-xs text-black font-semibold shrink-0 flex gap-1"
                }
                onClick={() => setIsCollapsed(prevState => !prevState)}
              >
                <span>{translate("plans_payment-bar_payment-options")}</span>
                <img
                  src={ChevronUp}
                  alt={""}
                  width={16}
                  height={16}
                  className={classNames({ "rotate-180": !isCollapsed })}
                />
              </button>
            </div>
            {isCollapsed ? (
              invoicesEnabled ? (
                <div className={"hidden"}>
                  {showInvoiceForm ? (
                    <InvoiceFrom
                      locale={locale}
                      translate={translate}
                      ref={invoiceFromRef}
                    />
                  ) : null}
                </div>
              ) : null
            ) : (
              <>
                {hasSubtractedAmount ? (
                  <div className={"my-4"}>{detailPrice}</div>
                ) : null}
                {selectedPlanName}
                {paymentMethodPricing?.overpayment_amount &&
                Number(paymentMethodPricing.order_amount) === 0 ? (
                  <div>
                    <strong
                      className={"block text-sm text-bold text-black mt-4 mb-2"}
                    >
                      {translate("plans_payment-bar_overpayment-title")}
                    </strong>
                    <p
                      className={"text-xs text-black-8 mb-4 max-w-2xl.1"}
                      dangerouslySetInnerHTML={{
                        __html: translate(
                          "plans_payment-bar_overpayment-paragraph",
                          {
                            overpaymentAmount: FormatCurrency({
                              locale,
                              value: paymentMethodPricing.overpayment_amount
                            })
                          }
                        )
                      }}
                    ></p>
                  </div>
                ) : null}
                {currentCurrencyOfferByPeriod && !isFree ? (
                  <div>
                    {hasSupportedPaymentMethod ? (
                      <div
                        className={"text-xs text-black opacity-65 mb-3 mt-6"}
                      >
                        {translate("plans_payment-bar_payment_form")}
                      </div>
                    ) : null}
                    <div className={"flex flex-col gap-4"}>
                      {hasSupportedPaymentMethod ? (
                        availablePaymentMethods.map(supportedPaymentMethod => {
                          return (
                            <PaymentMethod
                              key={supportedPaymentMethod.name}
                              fullWidth
                              onSelect={() =>
                                setSelectedPaymentMethod(supportedPaymentMethod)
                              }
                              images={supportedPaymentMethod.children}
                              image={{
                                src: supportedPaymentMethod.image,
                                alt: supportedPaymentMethod.name
                              }}
                              name={supportedPaymentMethod.name}
                              isSelected={
                                selectedPaymentMethod?.name ===
                                supportedPaymentMethod.name
                              }
                            />
                          );
                        })
                      ) : (
                        <strong className={"text-xs text-black font-semibold"}>
                          {translate("plans_payment-bar_no-payment-method")}
                        </strong>
                      )}
                    </div>
                  </div>
                ) : null}
                {invoicesEnabled ? (
                  <div className={"mt-6"}>
                    <Checkbox
                      label={translate("plans_payment-bar_invoice")}
                      className={"text-black-8"}
                      checked={showInvoiceForm}
                      onClick={() => {
                        setShowInvoiceForm(prevState => !prevState);
                      }}
                    />
                  </div>
                ) : null}
                {invoicesEnabled && showInvoiceForm ? (
                  <div className={"mt-6 mb-2"}>
                    <InvoiceFrom
                      locale={locale}
                      translate={translate}
                      ref={invoiceFromRef}
                    />
                  </div>
                ) : null}
                <div className={"my-6"}>{description}</div>
              </>
            )}
            <PaymentButton
              className={"button button_full-width ml-auto shrink-0 mt-4 p-4"}
              selectedPlan={selectedPlan}
              onPaymentButtonClick={onPaymentButtonClick}
              userData={userData}
              setShowModal={setShowModal}
              selectedPaymentMethod={selectedPaymentMethod}
              paymentFormLoading={paymentFormLoading}
              paymentDetails={paymentDetails}
              currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
              invoiceFromRef={invoiceFromRef}
              showInvoiceForm={showInvoiceForm}
              onValidationFailure={() => setIsCollapsed(false)}
            />
          </div>
        </form>
        {showModal
          ? createPortal(
              <PaymentModal
                selectedPlanName={selectedPlanName}
                price={detailPrice}
                description={description}
                selectedPlan={selectedPlan}
                onPaymentButtonClick={async () => {
                  const invoiceFormData = await resolveInvoiceFormDataForPayment(
                    {
                      showInvoiceForm,
                      invoiceFormRef: invoiceFromRef
                    }
                  );

                  if (invoiceFormData === false) {
                    return;
                  }

                  onPaymentButtonClick({ invoiceFormData });
                }}
                hideModal={() => setShowModal(false)}
              />,
              document.body
            )
          : null}
      </LandingTypography>
    </>
  );
};

export default compose(
  withTranslations,
  withRouter,
  withUserData,
  withInvoices
)(PaymentBarCompacted);
